import React, { ReactNode, useEffect } from 'react';
import { Form, Button, Table, Row, Col } from 'react-bootstrap';
import { Link, useForm, usePage } from '@inertiajs/react';

import AuthedLayout from '../../Layouts/Authed';
import Valuation from '../../Data/Valuation';
import CarrierRetentionRate from '../../Data/CarrierRetentionRate';
import CarrierRetentionRateYear from '../../Data/CarrierRetentionRateYear';
import { getEventValue, replaceNullWithEmpty } from './Helpers/RepeatingForms';
import { range } from '../../utils';
import FormHeader from '../../Components/Forms/FormHeader';
import CarrierRetentionRateRow from './CarrierRetentionRates/CarrierRetentionRateRow';
import DecimalInput from '../../Components/Forms/DecimalInput';
import Tooltip from '../../Components/Forms/Tooltip';
import AutosaveStatus from '../../Components/Forms/AutosaveStatus';
import useValuationStepAutosave from './Helpers/useValuationStepAutosave';
import MarkComplete from '../../Components/Forms/MarkComplete';
import { Role } from '../../Data/Role';

interface Props {
	valuation: Valuation;
	carrier_retention_rates: CarrierRetentionRate[];
	maxCarrierRetentionRatesPerValuation: number;
	currentYear: number;
}

interface FormData {
	total_agency_retention_rate: number;
	carrier_retention_rates: Partial<CarrierRetentionRate>[];
	mark_complete: boolean;
}

const CarrierRetentionRates = (props: Props) => {
	const { errors, readOnly, user } = usePage<any>().props;
	const isAdmin = user?.role === Role.admin;

	const { data, setData, post, processing } = useForm<FormData>({
		total_agency_retention_rate: props.valuation.total_agency_retention_rate ?? '',
		carrier_retention_rates:
			replaceNullWithEmpty(props.carrier_retention_rates) ?? [],
		mark_complete: props.valuation.completed_steps?.includes('carrier-retention-rates') ?? false,
	});
	const autosaveStatus = useValuationStepAutosave('carrier-retention-rates', props.valuation.id, data);

	const submit = (event: any) => {
		event.preventDefault();
		post(`/valuation-form/${props.valuation.id}/carrier-retention-rates`);
	};

	const updateIndex = (event: React.ChangeEvent<any>, index: number) => {
		const newData = JSON.parse(JSON.stringify(data));
		newData.carrier_retention_rates[index][event.target.name] =
			getEventValue(event);
		setData(newData);
	};

	const updateYear = (
		index: number,
		yearIndex: number,
		event: React.ChangeEvent<any>
	) => {
		const newData = JSON.parse(JSON.stringify(data));
		newData.carrier_retention_rates[index].years[yearIndex][event.target.name] =
			getEventValue(event);
		setData(newData);
	};

	useEffect(() => {
		scrollTo(0,0);
	}, []);

	return (
		<div>
			<FormHeader
				agencyName={props.valuation.agency_name}
				effectiveDate={props.valuation.effective_date}
				pageName='Retention Rate By Carrier - Top 5 Carriers'
			/>
			<Form onSubmit={submit} className='valuationForm'>
				<fieldset disabled={readOnly}>
				<Row className='py-4'>
					<Col md={4}>
						<DecimalInput
							name='total_agency_retention_rate'
							label={
								<>
									What is your total agency retention rate?
									<Tooltip id='total-retention-rate'>
										Use your best estimate. Check your AMS if they provide an agency-wide retention ratio and if so use that here.
									</Tooltip>
								</>
							}
							prefix={null}
							postfix='%'
							value={data.total_agency_retention_rate ?? ''}
							onChange={(event) =>
								setData({
									...data,
									total_agency_retention_rate: getEventValue(event),
								})
							}
							errors={errors}
							arrayName='valuation.total_agency_retention_rate'
							errorArrayName='valuation.total_agency_retention_rate'
							overrideArrayName={true}
							overrideErrorArrayName={true}
						/>
					</Col>
				</Row>
				<Table striped borderless hover responsive>
					<thead>
						<tr>
							<th
								id='CarrierRetentionRateNum'
								className='cell-xs sticky-cell'
							>
								#
							</th>
							<th id='carrier_name' className='cell-lg'>
								Carrier Name
							</th>
							{range(
								props.currentYear - 5,
								props.currentYear
							).map((year) => (
								<th id={`${year}-retention_rate`} key={year}>
									{year}
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{data.carrier_retention_rates.map((retentionRate, index) => (
							<CarrierRetentionRateRow
								key={`CarrierRetentionRate-${index}`}
								currentYear={props.currentYear}
								retentionRate={retentionRate}
								index={index}
								errors={errors}
								updateIndex={updateIndex}
								updateYear={updateYear}
							/>
						))}
					</tbody>
				</Table>
				</fieldset>

				<div className='d-flex justify-content-between align-items-center'>
					<Button
						as={Link as any}
						href={`/valuation-form/${props.valuation.id}/carrier-loss-ratios`}
						variant='outline-primary'
						className='mt-4'
					>
						Back
					</Button>
					<div className='d-flex align-items-center'>
						{!readOnly && <AutosaveStatus status={autosaveStatus} />}
						{!readOnly && (
							<MarkComplete
								checked={data.mark_complete}
								onChange={(checked) => setData('mark_complete', checked)}
								className='mt-4 ms-4'
							/>
						)}
						{readOnly ? (
							<Button
								as={Link as any}
								href={`/valuation-form/${props.valuation.id}/pl-statement-upload`}
								className='mt-4 ms-4'
							>
								Next
							</Button>
						) : (
							<Button type='submit' className='mt-4 ms-4'>
								Next
							</Button>
						)}
					</div>
				</div>
			</Form>
		</div>
	);
};

CarrierRetentionRates.layout = (page: ReactNode) => (
	<AuthedLayout>{page}</AuthedLayout>
);

export default CarrierRetentionRates;
